import { useCallback, useEffect, useState } from "react";
import { Col, Modal, Row, Spinner } from "react-bootstrap";
import { useTotalFactura } from "../../hook/useTotalFactura";
import { useSelector } from "react-redux";
import { Billing, SendPayloadContifico } from "../../Service/Service.billing";
import Swal from "sweetalert2";
import CuerpoEmitir from "./Crear/CuerpoEmitir";
import "../../styles/FacturaCrear.css";
import { Listarusers, ListaruserSinPagineo } from "../../Service/Service.users";
import SelectSearchable from "../SelectSeachble/SelectSearchble";
import { usePacienteSeguro } from "../../hook/usePacienteSeguro";

// HM derivado (idempotencia): una unica clave por intento logico de
// facturacion (una apertura del modal de confirmacion). Un reintento del
// MISMO intento (ej. doble click en "Generar", o retry tras timeout de red
// sin haber cerrado el modal) reutiliza la misma key -- el backend la usa
// para no crear una segunda factura. Cerrar y reabrir el modal (una
// intencion nueva del usuario) genera una key nueva.
const generateIdempotencyKey = () => {
    if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
    return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

function ConfirmBilling({ data }) {

    const { show, handleClose, clienteSeleccionado, pacienteSeleccionado, handleCancelBilling, infoAdicional } = data;

    const [loading, setLoading] = useState(false);
    const [selected, setSelected] = useState("FAC");
    const [disabled, setDisabled] = useState(false);
    const [idempotencyKey, setIdempotencyKey] = useState(() => generateIdempotencyKey());
    
    const { subtotal, iva, total } = useTotalFactura();
    const datoPaciente  = usePacienteSeguro();
    
    const formasPago  = useSelector((state) => state.formapagoredux.pagos ) || [];
    const procedimientos = useSelector((state) => state.facturaredux.procedimientos) || [];
    const [vendedores, setVendedores] = useState([]);
    const [selectedVendedorOption, setSelectedVendedorOption] = useState(null);
    
    const tienePlan = procedimientos.some(p => p.plan);
    /*console.log(JSON.stringify(procedimientos));
    alert(JSON.stringify(procedimientos));*/
    
    const handleSelect = (type) => {
        setSelected(type);
    };

    const handleBilling = async () => {
        setLoading(true);

        try {
            const data = {
                cabecera: {
                    subtotal,
                    iva,
                    total,
                    paciente_id: datoPaciente.paciente ? datoPaciente.paciente.value : null,
                    seguro_id: datoPaciente.seguro ? datoPaciente.seguro.value : null,
                    cliente_id: clienteSeleccionado ? clienteSeleccionado.value : null,
                    documento: selected,
                    info_adicional_1: infoAdicional ? infoAdicional : "",
                    vendedor_id: selectedVendedorOption ? selectedVendedorOption.value : null
                },
                detalle: procedimientos.map(det => ({
                    cantidad: det.cantidad,
                    precio: det.precio,
                    idprocedimiento_cita_paciente: det.idprocedimiento_cita_paciente,
                    idExamenTipo: det.idExamenTipo,
                    contifico_id: det.ExamenTipo?.contifico_id ?? null,
                    plan:
                        det?.plan && det?.plan?.id_plan != null
                            ? {
                                id_plan: det.plan.id_plan,
                                precio: det.plan.precio_empresa ?? null,
                                seguro_id: det.plan.tarifario?.seguro_id ?? null,
                                id_tipo_examen: det.plan.id_tipo_examen ?? null
                              }
                            : null
                })),
                pago: formasPago.map(pag => ({
                    id: pag.value,
                    monto: pag.montoPago,
                    pago: pag.label,
                    banco: pag.datosBancario ? pag.datosBancario : null,
                }))
            };
            
            console.log("antes de billing, lo que se envia al backend..." + JSON.stringify(data));
            
            const response = await Billing(data, idempotencyKey);

            if (!response.success) {
                throw new Error(response.message || "Error al crear factura / comprobante");
            }

            const idfactura = response?.local_invoice?.idfactura;

            // COBERTURAS-FLOW-FORWARD-02: una resolucion PURA SEGURO100 (ver
            // resolution_type "SEGURO100") no crea ninguna factura/comprobante
            // local -- backend no arma payload de Contifico para ese caso, asi
            // que aqui tampoco se envia nada a Contifico. `response.message`
            // ya trae el mensaje especifico exigido ("Cobertura procesada
            // correctamente...") armado por el backend.
            // CONTIFICO-LOCAL-QA-GUARD-01 (TEMPORAL, default-OFF): con
            // REACT_APP_SKIP_CONTIFICO_SEND="true" en .env local, el envio
            // automatico a Contifico se salta por completo -- la factura/
            // comprobante local (arriba, `Billing`) ya se creo igual. Sin la
            // variable (o distinta de "true") el comportamiento es identico
            // al de siempre.
            const skipContificoSend = process.env.REACT_APP_SKIP_CONTIFICO_SEND === "true";

            // FACTURA-COMPROBANTE-CONTIFICO: un COMPROBANTE no es un documento
            // fiscal -- funciona como ingreso de caja: lleva su propia
            // numeracion y se guarda localmente, nada mas. Solo la FACTURA va
            // a Contifico.
            //
            // Esto NO cambia lo que ocurria de hecho: de los 131 comprobantes
            // emitidos, NINGUNO tiene `id_contifico` (frente a 2084 de 2355
            // facturas). El envio se intentaba y no cuajaba nunca. Lo que
            // cambia es que ya no se hace una llamada condenada a fallar, ni
            // se aborta el flujo por su error.
            const esFactura = selected === "FAC";

            if (idfactura && esFactura && !skipContificoSend) {
                const contifico = await SendPayloadContifico({ idfactura });

                if (!contifico.success) {
                    throw new Error(contifico.message || "Error al enviar factura a Contífico desde backend");
                }

                if (contifico.data?.already_sent) {
                    response.message += "\n Factura ya enviada previamente a Contífico.";
                } else {
                    response.message += "\n ID Contífico: " + (contifico.data?.id_contifico || "GENERADO");
                }
            } else if (idfactura && skipContificoSend) {
                response.message += "\n [QA local] Envío a Contífico deshabilitado (REACT_APP_SKIP_CONTIFICO_SEND=true).";
            }

            handleClose();
            handleCancelBilling();

            Swal.fire({
                icon: "success",
                title: "Éxito",
                text: response.message,
                showConfirmButton: true,
            });
            
        } catch (response) {
            Swal.fire({
                icon: "error",
                title: "Error",
                text: response.message || "Hubo un error al agregar el procedimiento",
            });            
 
        } finally {
            setLoading(false);
        }
    }

    // eslint-disable-next-line no-unused-vars
    const handleVendedorChange = selectedOption => {
        setSelectedVendedorOption(selectedOption);
    };

    const fetchInitialData = useCallback(async () => {
        
        if (show) {
            const vendedores = await ListaruserSinPagineo();
            if (vendedores && vendedores.success) {
                const listVendedores = vendedores?.data.map((vendedor) => ({ value: vendedor.id, label: vendedor.full_name, ...vendedor }));
                setVendedores(listVendedores);
            }

            // COBERTURAS-FLOW-FORWARD-02: una seleccion 100% cubierta por el
            // seguro (tienePlan && total===0) ya NO deshabilita el modal --
            // "Generar" resuelve la cobertura directamente (ver handleBilling).
            // El toggle FACTURA/COMPROBANTE sigue mostrandose para el caso
            // normal; en el caso 100% seguro el backend ignora `documento`
            // (no crea ningun documento local).
            setDisabled(false);
            setSelected("FAC");
        }
    }, [show, tienePlan, total]);

    useEffect(() => {
        fetchInitialData();
    }, [fetchInitialData]);

    // HM derivado (idempotencia): nueva key solo cuando el modal se ABRE
    // (nueva intencion del usuario) -- nunca en cada render, para que un
    // reintento dentro de la MISMA apertura (doble click, retry tras
    // timeout) reutilice la misma key.
    useEffect(() => {
        if (show) {
            setIdempotencyKey(generateIdempotencyKey());
        }
    }, [show]);

    // FACTURA-UI FASE 5.2: cambia SOLO la presentacion. `handleBilling`, la
    // clave de idempotencia, la carga de vendedores y la condicion del boton
    // quedan exactamente como estaban.
    const faltaVendedor = !(tienePlan && total == 0) && !selectedVendedorOption;
    const puedeGenerar = !loading && !faltaVendedor;
    const etiquetaTipo = selected === "COM" ? "comprobante" : "factura";

    return (
        <Modal show={show} backdrop="static" onHide={handleClose} keyboard={false} className="femitir">
            <Modal.Header closeButton>
                <Modal.Title className="femitir__titulo">Emitir documento</Modal.Title>
            </Modal.Header>

            <Modal.Body className="femitir__cuerpo">
                <CuerpoEmitir
                    selected={selected}
                    onSelect={handleSelect}
                    disabled={disabled}
                    vendedores={vendedores}
                    selectedVendedorOption={selectedVendedorOption}
                    onVendedor={handleVendedorChange}
                    faltaVendedor={faltaVendedor}
                    paciente={
                        datoPaciente?.paciente?.label ||
                        datoPaciente?.seguro?.label ||
                        clienteSeleccionado?.label ||
                        ""
                    }
                    procedimientos={procedimientos}
                    formasPago={formasPago}
                    total={total}
                />
            </Modal.Body>

            <Modal.Footer className="femitir__pie">
                <p className="femitir__aviso">Una vez emitida no se puede editar.</p>
                <div className="femitir__acciones">
                    <button type="button" className="fcrear__btn" onClick={handleClose} disabled={loading}>
                        Cancelar
                    </button>
                    <button
                        type="button"
                        className="fcrear__btn fcrear__btn--primario"
                        disabled={!puedeGenerar}
                        onClick={handleBilling}
                        title={faltaVendedor ? "Selecciona un vendedor para continuar" : undefined}
                    >
                        {loading ? (
                            <>
                                <Spinner as="span" animation="grow" size="sm" role="status" aria-hidden="true" />
                                {" "}Por favor espere...
                            </>
                        ) : (
                            `Emitir ${etiquetaTipo}`
                        )}
                    </button>
                </div>
                {/* El boton apagado SIEMPRE dice por que. */}
                {faltaVendedor && !loading && (
                    <p className="femitir__pie-motivo">Falta seleccionar el vendedor</p>
                )}
            </Modal.Footer>
        </Modal>
    )
}

export default ConfirmBilling;
